import React, { useRef } from "react";
import { animate, stagger } from "animejs";
import { FaRobot, FaCloud, FaBrain, FaDatabase, FaCode, FaNetworkWired } from "react-icons/fa";
import useAnimeScope from "../../hooks/useAnimeScope";
import useAnimeReveal from "../../hooks/useAnimeReveal";
import "./Skills.scss";

const skillCategories = [
  {
    icon: <FaRobot />,
    title: 'Automation & Orchestration',
    skills: [
      { name: 'n8n', accent: true },
      { name: 'Workflow Design' },
      { name: 'Multi-Agent Systems', accent: true },
      { name: 'Agentic Consensus Patterns' },
      { name: 'Make.com' },
      { name: 'Event-Driven Architecture' },
      { name: 'MCP Tools' },
    ],
  },
  {
    icon: <FaBrain />,
    title: 'AI & LLM Engineering',
    skills: [
      { name: 'RAG Pipelines', accent: true },
      { name: 'PGVector / pgvector' },
      { name: 'Gemini API', accent: true },
      { name: 'Mistral AI / OCR' },
      { name: 'OpenAI GPT-4' },
      { name: 'Vectorless RAG Engine' },
      { name: 'Semantic Chunking' },
      { name: 'LangChain' },
    ],
  },
  {
    icon: <FaCode />,
    title: 'Backend & APIs',
    skills: [
      { name: 'FastAPI', accent: true },
      { name: 'Python' },
      { name: 'Frappe / ERPNext', accent: true },
      { name: 'Node.js / Express' },
      { name: 'REST & Webhook APIs' },
      { name: 'Jinja2 Templates' },
      { name: 'Alembic Migrations' },
    ],
  },
  {
    icon: <FaCloud />,
    title: 'Cloud & Infrastructure',
    skills: [
      { name: 'AWS Lambda', accent: true },
      { name: 'AWS SAM / CloudFormation' },
      { name: 'Docker', accent: true },
      { name: 'Redis' },
      { name: 'AWS S3 / EC2' },
      { name: 'AWS Certified Cloud Practitioner' },
    ],
  },
  {
    icon: <FaDatabase />,
    title: 'Data & Scraping',
    skills: [
      { name: 'PostgreSQL', accent: true },
      { name: 'MySQL' },
      { name: 'Playwright', accent: true },
      { name: 'Selenium' },
      { name: 'Pandas' },
      { name: 'ClamAV / Security' },
      { name: 'TypeORM / Prisma' },
    ],
  },
  {
    icon: <FaNetworkWired />,
    title: 'Integrations & Platforms',
    skills: [
      { name: 'HubSpot CRM', accent: true },
      { name: 'Google Workspace' },
      { name: 'WhatsApp Cloud API' },
      { name: 'Aircall' },
      { name: 'ElevenLabs' },
      { name: 'PrestaShop' },
      { name: 'Trello / OneDrive' },
      { name: 'LinkedIn & Facebook APIs' },
    ],
  },
];

const Skills = () => {
  const rootRef = useRef(null);

  useAnimeReveal(rootRef, "[data-reveal]", []);

  useAnimeScope(rootRef, () => {
    const cards = rootRef.current.querySelectorAll(".skills__category");

    cards.forEach((card) => {
      const tags = card.querySelectorAll(".skill-tag");

      const handleEnter = () => {
        animate(tags, {
          translateY: [0, -4, 0],
          delay: stagger(26),
          duration: 420,
          ease: "outExpo"
        });
      };

      card.addEventListener("mouseenter", handleEnter);
      card._animeCleanup = () => card.removeEventListener("mouseenter", handleEnter);
    });

    return () => {
      cards.forEach((card) => card._animeCleanup?.());
    };
  }, []);

  return (
    <section id="skills" className="skills" ref={rootRef}>
      <div className="skills__content">
        <h2 className="skills__heading" data-reveal>Skills & Expertise</h2>

        <div className="skills__grid">
          {skillCategories.map((cat) => (
            <article className="skills__category" key={cat.title} data-reveal>
              <div className="skills__category-header">
                <div className="cat-icon">{cat.icon}</div>
                <h3>{cat.title}</h3>
              </div>

              <div className="skills__tags" role="list" aria-label={`${cat.title} skills`}>
                {cat.skills.map((skill) => (
                  <span
                    key={skill.name}
                    className={`skill-tag${skill.accent ? " skill-tag--accent" : ""}`}
                    role="listitem"
                  >
                    {skill.name}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
